import React, {useEffect, useState} from 'react';
import {Controller, useFormContext} from 'react-hook-form';
import {defineMessages, useIntl} from 'react-intl';
import {Button} from '@igp/shared/components/button';
import {Form, FormControlGroup} from '@igp/shared/components/form';
import {LocalizedText} from '@igp/shared/components/i18n';
import {
    DateSinglePickerTemporary,
    FormError,
    FormTextInputDefault,
    numberRegex,
    RuleType,
    StyledToggle,
    useValidationFormatter,
} from '@igp/shared/components/input';
import {BulkModalTitle, ModalFooter} from '@igp/shared/components/modal';
import type {SelectOption} from '@igp/shared/components/select';
import {AgentAutocomplete} from '@igp/shared/features/agent-profile';
import {useCurrencySymbol} from '@igp/shared/intl';
import {getNonEmptyValueValidator} from '@igp/shared/redux/view';
import {useNumberFormatter} from '@igp/shared/utils';
import {EntityType} from '@models';
import {Typography} from '@mui/material';

import type {AgentLevelReportV2ServerFilterKeys} from '@features/agent-level-report-v2';
import {useNegativeCarryoverReport} from '@features/negative-carryover-report';

import {firstAvailableDate} from '../constants';
import type {AdjustDebtFormModel, AdjustDebtTypeViewModel} from '../types';

const localized = defineMessages({
    title: {
        id: 'AdjustDebtForm_title',
        defaultMessage: 'Adjust Debt',
    },
    amountField: {
        id: 'AdjustDebtForm_amountField',
        defaultMessage: 'Amount',
    },
    deductType: {
        id: 'AdjustDebtForm_deductType',
        defaultMessage: 'Deduct Debt',
    },
    addType: {
        id: 'AdjustDebtForm_addType',
        defaultMessage: 'Add Debt',
    },
    cancel: {
        id: 'AdjustDebtForm_cancel',
        defaultMessage: 'Cancel',
    },
    submit: {
        id: 'AdjustDebtForm_submit',
        defaultMessage: 'Submit',
    },
    currentDebt: {
        id: 'AdjustDebtForm_currentDebt',
        defaultMessage: 'Current debt: {amount}',
    },
    systemLimit: {
        id: 'AdjustDebtForm_systemLimit',
        defaultMessage: 'Amount exceeds system limit. Please enter a smaller value.',
    },
    deduction: {
        id: 'AdjustDebtForm_deduction',
        defaultMessage: 'Deduction',
    },
    agentId: {
        id: 'AdjustDebtForm_agentId',
        defaultMessage: 'Agent ID',
    },
    adjustmentType: {
        id: 'AdjustDebtForm_adjustmentType',
        defaultMessage: 'Adjustment Type',
    },
    description: {
        id: 'AdjustDebtForm_description',
        defaultMessage: 'Enter the agent information and adjustment details.',
    },
    transferDate: {
        id: 'AdjustDebtForm_transferDate',
        defaultMessage: 'Adjustment Date',
    },
    agentNotFound: {
        id: 'AdjustDebtForm_agentNotFound',
        defaultMessage: 'Agent not found. Please select a valid agent from the list.',
    },
});

export const adjustTypeOptions: SelectOption<AdjustDebtTypeViewModel>[] = [
    {
        value: 'Deduct',
        label: localized.deductType,
    },
    {
        value: 'Add',
        label: localized.addType,
    },
];

type AdjustDebtFormProps = {
    onSubmit?: () => void;
    onCancel?: () => void;
};

export function AdjustDebtForm({onCancel, onSubmit}: AdjustDebtFormProps) {
    const {formatMessage} = useIntl();
    const {formatNumber} = useNumberFormatter();
    const {setValue, trigger, control, formState, watch} = useFormContext<AdjustDebtFormModel>();
    const validationFormatter = useValidationFormatter();
    const currencySymbol = useCurrencySymbol();
    const agentUid = watch('agentUid');
    const [agentInput, setAgentInput] = useState<string>(agentUid);
    const availableDateRangeForSelection = {from: firstAvailableDate.toDate()};
    const {items, isDataLoading} = useNegativeCarryoverReport({
        fields: ['debtAfterPayment', 'agentUid', 'id'],
        viewType: 'AdjustDebtForm',
        defaultFilters: [
            {
                key: 'agent_uid',
                value: agentUid,
            },
        ],
        defaultPaging: {page: 1, pageSize: 1},
        blockFetchWithInvalidFilter: true,
        validateFilter: getNonEmptyValueValidator<AgentLevelReportV2ServerFilterKeys>('agent_uid'),
        saveFilters: false,
    });
    const currentDebt = items?.[0]?.debtAfterPayment ?? 0;
    const formattedCurrentDebt = formatNumber(currentDebt, {format: 'USD'});

    useEffect(() => {
        trigger('agentUid');
    }, [agentInput]);

    useEffect(() => {
        if (!isDataLoading) {
            setValue('currentDebt', currentDebt);
        }
    }, [isDataLoading, currentDebt]);

    useEffect(() => {
        trigger();
    }, []);
    return (
        <>
            <Form>
                <BulkModalTitle>
                    <>
                        <LocalizedText label={localized.title} />
                        <Typography variant="body1" color="secondary">
                            <LocalizedText label={localized.description} />
                        </Typography>
                    </>
                </BulkModalTitle>
                <FormControlGroup label={localized.agentId} labelVariant="caption" labelPlacement="top">
                    <Controller
                        control={control}
                        name="agentUid"
                        render={({field, fieldState}) => (
                            <>
                                <AgentAutocomplete
                                    viewType="AdjustDebtForm"
                                    value={field.value}
                                    onChange={(uid, username) => {
                                        field.onChange(uid);
                                        setValue('username', username);
                                    }}
                                    onInputChange={setAgentInput}
                                    agentProfileEntity={EntityType.AgentProfile}
                                    mode="default"
                                    searchBy="uid"
                                />
                                {fieldState.error?.message && <FormError>{fieldState.error.message}</FormError>}
                            </>
                        )}
                        rules={{
                            validate: value => {
                                let message: string | true = true;
                                if (!value && agentInput) {
                                    message = formatMessage(localized.agentNotFound);
                                } else if (!value && !agentInput) {
                                    message = validationFormatter(RuleType.Required, localized.agentId);
                                }

                                return message;
                            },
                        }}
                    />
                </FormControlGroup>
                {formattedCurrentDebt && (
                    <Typography variant="body1">
                        <LocalizedText
                            label={localized.currentDebt}
                            labelParams={{
                                amount: formattedCurrentDebt,
                            }}
                        />
                    </Typography>
                )}
                <FormControlGroup label={localized.adjustmentType} labelVariant="caption" labelPlacement="top">
                    <Controller
                        name="adjustType"
                        control={control}
                        render={({field}) => <StyledToggle value={field.value} options={adjustTypeOptions} onChange={field.onChange} />}
                    />
                </FormControlGroup>
                <FormControlGroup label={localized.amountField} labelVariant="caption" labelPlacement="top">
                    <Controller
                        render={({field, fieldState}) => (
                            <FormTextInputDefault
                                endAdornment={currencySymbol}
                                type="number"
                                onChange={field.onChange}
                                value={field.value}
                                fieldState={fieldState}
                                hasBottomSpacing={false}
                            />
                        )}
                        rules={{
                            max: {
                                value: 999_999,
                                message: formatMessage(localized.systemLimit),
                            },
                            min: {
                                value: 0.01,
                                message: validationFormatter(RuleType.GreaterThan, localized.amountField, 0),
                            },
                            required: {
                                value: true,
                                message: validationFormatter(RuleType.Required, localized.amountField),
                            },
                            pattern: {
                                value: numberRegex,
                                message: validationFormatter(RuleType.Number, localized.amountField),
                            },
                        }}
                        control={control}
                        name="amount"
                    />
                </FormControlGroup>
                <FormControlGroup label={localized.transferDate} labelVariant="caption" labelPlacement="top">
                    <Controller
                        name="transferDate.seconds"
                        control={control}
                        render={({field, fieldState}) => (
                            <div>
                                <DateSinglePickerTemporary
                                    onChange={field.onChange}
                                    value={field.value}
                                    hasBottomSpacing={false}
                                    mode="start"
                                    availableDateRangeForSelection={availableDateRangeForSelection}
                                    disableFutureDates
                                />
                                {fieldState.error?.message && <FormError>{fieldState.error.message}</FormError>}
                            </div>
                        )}
                        rules={{
                            required: validationFormatter(RuleType.Required, localized.transferDate),
                        }}
                    />
                </FormControlGroup>
            </Form>
            <ModalFooter>
                <Button label={localized.cancel} onClick={onCancel} />
                <Button label={localized.submit} onClick={onSubmit} color="primary" disabled={!formState.isValid || isDataLoading} />
            </ModalFooter>
        </>
    );
}
